import { ArrowIcon, FileIcon } from "components/icons";
import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { GetUserReportByCityIdQueryVariables, UpdateUserReportInput } from "@/types/gql/graphql";
import { PaginatedUserReportArgs, PaginatedUserReports, UserReport } from "types/user-report.type";
import { SkipToken, skipToken } from "@apollo/client/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CityContext } from "contexts/city-context-provider";
import { HeaderItem } from "@/components/data-table/types/table.types";
import { Pagination } from "components/data-table/Pagination";
import { ReportFilesModal } from "./file-viewer-modal";
import { SortOrder } from "@/types/gql/graphql";
import { TableSkeleton } from "components/data-table";
import { cn } from "@/lib/utils";
import { defaultUserReportCells } from "./user-report.const";
import { isEmpty } from "helper/utils";
import { useUpdateUserReport } from "../hooks/use-query-user-report";

interface UserReportListingProps {
  isLoading: boolean;
  userReportList: PaginatedUserReports;
  selectedUserReport: UserReport | null;
  onSelectUserReport: (input: UserReport) => void;
  setUserReportParams: Dispatch<SetStateAction<PaginatedUserReportArgs>>;
  userReportParams: PaginatedUserReportArgs;
  close?: () => void;
}

export const UserReportListing = ({
  isLoading,
  userReportList,
  onSelectUserReport,
  selectedUserReport,
  setUserReportParams,
  userReportParams,
}: UserReportListingProps) => {
  const [headerCells, setHeaderCells] = useState<HeaderItem<keyof UserReport>[]>(defaultUserReportCells);
  const [files, setFiles] = useState<string[]>([]);
  const { currentCity } = useContext(CityContext);

  const userReportVariables: GetUserReportByCityIdQueryVariables | SkipToken = useMemo(() => {
    if (currentCity?.id) {
      return { ...userReportParams, cityId: currentCity.id };
    }
    return skipToken;
  }, [currentCity?.id, userReportParams]);

  const { updateUserReport } = useUpdateUserReport(userReportVariables);

  const userReportListSorted: UserReport[] = useMemo(() => {
    if (isLoading) {
      return [];
    }
    return userReportList.results;
  }, [isLoading, userReportList]);

  if (!currentCity) {
    return <EmptyData />;
  }

  const handleSort = (orderBy?: keyof UserReport, direction?: SortOrder) => {
    if (!orderBy || !direction) {
      return;
    }
    const newCells = headerCells.map((cell) => {
      if (cell.orderBy === orderBy) {
        const newDirection = direction === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc;
        return {
          ...cell,
          sortDirection: newDirection,
        };
      }
      return cell;
    });
    setUserReportParams((prevState) => ({ ...prevState, sortField: orderBy, sortOrder: direction }));
    setHeaderCells(newCells);
  };

  const onChangePage = (page: number) => {
    setUserReportParams((prevState) => ({ ...prevState, pageIndex: page }));
  };

  const changeStatus = async (input: UserReport) => {
    const newUserReport: UpdateUserReportInput = {
      id: input.id,
      isDone: !input.isDone,
    };
    await updateUserReport({
      variables: {
        reportInput: newUserReport,
      },
    });
  };

  return (
    <>
      <div className="flex flex-col w-full h-full gap-y-2 bg-white p-7">
        <div className="flex justify-between mb-11 items-center">
          <span className="font-bold text-lg">User Report</span>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          <Table>
            <TableHeader>
              <TableRow>
                {headerCells.map((item, idx) => (
                  <TableHead className={item.className} key={`user-report-table-header-${idx}`}>
                    {item.title}
                    {item.sortable && (
                      <Button variant="ghost" onClick={() => handleSort(item.orderBy, item.sortDirection)}>
                        <ArrowIcon
                          className={cn("size-2", item.sortDirection === SortOrder.Asc ? "-rotate-90" : "rotate-90")}
                        />
                      </Button>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="min-h-fit max-h-full">
              {isLoading ? (
                <TableSkeleton />
              ) : (
                <>
                  {isEmpty(userReportListSorted) ? (
                    <GenNoRecord colSpan={headerCells.length} message="No results found." />
                  ) : (
                    userReportListSorted.map((item) => {
                      return (
                        <TableRow
                          className={`hover:none ${selectedUserReport?.id === item.id ? "bg-muted/50" : "bg-white"}`}
                          key={`user-report-list-${item.id}`}
                        >
                          <TableCell className="text-sm px-6 py-3 justify-end w-44">{item.id}</TableCell>
                          <TableCell
                            className="text-sm px-6 py-3 cursor-pointer"
                            onClick={() => onSelectUserReport(item)}
                          >
                            {item.note}
                          </TableCell>
                          <TableCell
                            className={`text-sm px-6 py-3 flex justify-center items-center w-44 ${item.attachedFilesUrls?.length && "cursor-pointer"}`}
                            onClick={() => {
                              if (item.attachedFilesUrls?.length) {
                                setFiles(item.attachedFilesUrls);
                              }
                            }}
                          >
                            {item.attachedFilesUrls?.length ? (
                              <FileIcon
                                className={`${selectedUserReport?.id === item.id ? "text-gray-200" : "text-gray-900"}`}
                              />
                            ) : null}
                          </TableCell>
                          <TableCell className="text-sm px-6 py-3 justify-end w-44">
                            <div className="flex justify-center">
                              <Checkbox checked={item.isDone} onClick={() => changeStatus(item)} />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mb-2 flex flex-col items-end">
          <Pagination
            onChangePage={onChangePage}
            currentPage={userReportParams.pageIndex}
            totalPage={userReportList.totalPages}
          />
        </div>
      </div>
      {files.length > 0 && (
        <ReportFilesModal
          header="Report files"
          fileUrls={files}
          isOpen={!!files.length}
          onDismiss={() => setFiles([])}
        />
      )}
    </>
  );
};

interface GenNoRecordProps {
  colSpan?: number;
  message?: string;
}

export const GenNoRecord = ({ colSpan = 5, message = "No results found" }: GenNoRecordProps) => {
  return (
    <tr className="bg-default">
      <td className="px-6 py-4 whitespace-nowrap bg-white	 text-primary" colSpan={colSpan}>
        <div className="flex items-center justify-center">{message}</div>
      </td>
    </tr>
  );
};

export const EmptyData = () => (
  <div className="flex flex-col w-full h-full gap-y-2 bg-white p-7">
    <div className="flex justify-between mb-11 items-center">
      <span className="font-bold text-lg">User Report</span>
    </div>
    <div className="flex-1 overflow-y-auto min-h-0">
      <Table>
        <TableHeader>
          <TableRow>
            {defaultUserReportCells.map((item, idx) => (
              <TableHead className={item.className} key={`user-report-skeleton-header-${idx}`}>
                {item.title}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className="min-h-fit max-h-full">
          <GenNoRecord message="Please select a city" colSpan={4} />
        </TableBody>
      </Table>
    </div>
  </div>
);
